import type { GenogramInsightPayload } from '../../../infrastructure/ai/schemas';

/** Avoids i18next `TFunction` namespace brand clashes with `useTranslation('genogram')`. */
export type InsightPanelTranslateFn = (key: string, options?: Record<string, string | number>) => string;

type GenogramInsightArtifact = NonNullable<GenogramInsightPayload['artifacts']>[number];
type GenogramInsightSection = NonNullable<GenogramInsightPayload['sections']>[number];

export type InsightReportType = 'analysis' | 'evidence' | 'report_preview';

export type InsightGraphNode = {
  id: string;
  type?: string;
  name?: string;
  attrs?: Record<string, unknown>;
  props?: Record<string, unknown>;
  sex?: string;
  deceased?: boolean;
  medicalConditionTags?: unknown;
  culturalHeritageTags?: unknown;
};

export type InsightGraphEdge = {
  id?: string;
  type?: string;
  from: string;
  to: string;
  attrs?: Record<string, unknown>;
};

export type DeterministicInsightData = {
  artifacts: GenogramInsightArtifact[];
  sections: GenogramInsightSection[];
  followUps: string[];
  peopleCount: number;
  relationshipCount: number;
  withHealth: number;
  disconnectedPeopleCount: number;
  missingNameCount: number;
  unknownSexCount: number;
};

const asTagArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry ?? '').trim())
      .filter((entry) => entry.length > 0);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return [value.trim()];
  }
  return [];
};

const topEntries = (counts: Map<string, number>, limit = 5): string[] =>
  Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([label, count]) => `${label} (${count})`);

const readTags = (node: InsightGraphNode, key: 'medicalConditionTags' | 'culturalHeritageTags'): string[] => {
  const direct = asTagArray(node[key]);
  if (direct.length > 0) return direct;

  const attrs = node.attrs ?? {};
  const props = node.props ?? {};
  const propsAttrs = (props.attrs as Record<string, unknown> | undefined) ?? {};
  return asTagArray(attrs[key] ?? props[key] ?? propsAttrs[key]);
};

const readBoolean = (node: InsightGraphNode, key: 'deceased'): boolean => {
  const direct = node[key];
  if (typeof direct === 'boolean') return direct;
  const attrs = node.attrs ?? {};
  const props = node.props ?? {};
  const propsAttrs = (props.attrs as Record<string, unknown> | undefined) ?? {};
  const value = attrs[key] ?? props[key] ?? propsAttrs[key];
  return value === true;
};

const normalizeSex = (value: unknown): 'male' | 'female' | 'unknown' => {
  const token = String(value ?? '').trim().toLowerCase();
  if (token === 'male' || token === 'm') return 'male';
  if (token === 'female' || token === 'f') return 'female';
  return 'unknown';
};

const readSex = (node: InsightGraphNode): 'male' | 'female' | 'unknown' => {
  if (node.sex !== undefined) return normalizeSex(node.sex);
  const attrs = node.attrs ?? {};
  const props = node.props ?? {};
  const propsAttrs = (props.attrs as Record<string, unknown> | undefined) ?? {};
  return normalizeSex(attrs.sex ?? attrs.gender ?? props.sex ?? props.gender ?? propsAttrs.sex ?? propsAttrs.gender);
};

const normalizeEdgeToken = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const isParentChildEdge = (edge: InsightGraphEdge): boolean => {
  const edgeTypeToken = normalizeEdgeToken(edge.type);
  if (
    edgeTypeToken === 'child' ||
    edgeTypeToken === 'parentchild' ||
    edgeTypeToken === 'biologicalchild' ||
    edgeTypeToken === 'adoptedchild' ||
    edgeTypeToken === 'stepchild' ||
    edgeTypeToken === 'fosterchild'
  ) {
    return true;
  }

  const subtype = normalizeEdgeToken(edge.attrs?.subtype);
  return (
    subtype === 'child' ||
    subtype === 'biologicalchild' ||
    subtype === 'adoptedchild' ||
    subtype === 'stepchild' ||
    subtype === 'fosterchild'
  );
};

export const buildDeterministicInsightData = (
  reportType: InsightReportType,
  nodes: InsightGraphNode[],
  edges: InsightGraphEdge[],
): DeterministicInsightData => {
  const people = nodes.filter((node) => node.type === 'person');
  const personIds = new Set(people.map((person) => person.id));
  const relationships = edges.filter((edge) => personIds.has(edge.from) && personIds.has(edge.to));

  const relationCounts = new Map<string, number>();
  relationships.forEach((edge) => {
    const key = String(edge.type ?? 'unknown');
    relationCounts.set(key, (relationCounts.get(key) ?? 0) + 1);
  });

  const healthCounts = new Map<string, number>();
  const cultureCounts = new Map<string, number>();
  let withHealth = 0;
  let withCulture = 0;
  let deceasedCount = 0;
  const unknownSexCount = people.filter((person) => readSex(person) === 'unknown').length;
  const missingNameCount = people.filter((person) => String(person.name ?? '').trim().length === 0).length;

  people.forEach((person) => {
    const healthTags = readTags(person, 'medicalConditionTags');
    const cultureTags = readTags(person, 'culturalHeritageTags');
    if (healthTags.length > 0) withHealth += 1;
    if (cultureTags.length > 0) withCulture += 1;
    if (readBoolean(person, 'deceased')) deceasedCount += 1;
    healthTags.forEach((tag) => healthCounts.set(tag, (healthCounts.get(tag) ?? 0) + 1));
    cultureTags.forEach((tag) => cultureCounts.set(tag, (cultureCounts.get(tag) ?? 0) + 1));
  });

  const parentLinked = new Set<string>();
  relationships
    .filter(isParentChildEdge)
    .forEach((edge) => {
      parentLinked.add(edge.from);
      parentLinked.add(edge.to);
    });

  const disconnectedPeople = people.filter((person) => !relationships.some((edge) => edge.from === person.id || edge.to === person.id));

  const artifacts: GenogramInsightArtifact[] = [
    { label: 'People', value: String(people.length) },
    { label: 'Relationships', value: String(relationships.length) },
    { label: 'Health profiles', value: `${withHealth}/${people.length}` },
    { label: 'Cultural profiles', value: `${withCulture}/${people.length}` },
  ];

  const analysisSections: GenogramInsightSection[] = [
    {
      title: 'Graph coverage',
      items: [
        `Relationship types: ${topEntries(relationCounts, 4).join(', ') || 'none'}`,
        `Disconnected people: ${disconnectedPeople.length}`,
        `Deceased marked: ${deceasedCount}`,
      ],
    },
    {
      title: 'Top health and culture signals',
      items: [
        `Health tags: ${topEntries(healthCounts, 5).join(', ') || 'none'}`,
        `Culture tags: ${topEntries(cultureCounts, 5).join(', ') || 'none'}`,
      ],
    },
  ];

  const evidenceItems = [
    missingNameCount > 0 ? `${missingNameCount} person records missing a name` : 'No missing names detected',
    unknownSexCount > 0 ? `${unknownSexCount} people have unknown sex marker` : 'No unknown sex markers detected',
    disconnectedPeople.length > 0
      ? `${disconnectedPeople.length} people are not connected by relationships`
      : 'All people are connected to at least one relationship',
    parentLinked.size < people.length
      ? `${people.length - parentLinked.size} people have no parent/child linkage`
      : 'Parent/child linkage exists for all people',
  ];

  const followUps = [
    'Add missing diagnosis ages and causes of death where known.',
    'Confirm uncertain relationship links for disconnected people.',
    'Normalize health/culture tags to your preferred controlled vocabulary.',
  ];

  const sections: GenogramInsightSection[] =
    reportType === 'evidence'
      ? [
          { title: 'Evidence gaps', items: evidenceItems },
          { title: 'Recommended validation steps', items: followUps.slice(0, 2) },
        ]
      : reportType === 'report_preview'
        ? [
            {
              title: 'Report outline',
              items: [
                'Family structure and key members',
                'Medical and cultural patterns',
                'Known uncertainties and next actions',
              ],
            },
            ...analysisSections,
          ]
        : analysisSections;

  return {
    artifacts,
    sections,
    followUps,
    peopleCount: people.length,
    relationshipCount: relationships.length,
    withHealth,
    disconnectedPeopleCount: disconnectedPeople.length,
    missingNameCount,
    unknownSexCount,
  };
};

// --- Insight panel i18n (English source strings originate in buildDeterministicInsightData above) ---

const ARTIFACT_LABEL_KEYS: Record<string, string> = {
  People: 'insights.artifacts.people',
  Relationships: 'insights.artifacts.relationships',
  'Health profiles': 'insights.artifacts.healthProfiles',
  'Cultural profiles': 'insights.artifacts.culturalProfiles',
  Commands: 'insights.artifacts.commands',
  'Adds/Removes': 'insights.artifacts.addsRemoves',
  'Updates/Moves': 'insights.artifacts.updatesMoves',
  'Generation source': 'insights.artifacts.generationSource',
  Output: 'insights.artifactLabels.output',
  Focus: 'insights.artifactLabels.focus',
  Format: 'insights.artifactLabels.format',
  Audience: 'insights.artifactLabels.audience',
  Perspective: 'insights.artifactLabels.perspective',
  Use: 'insights.artifactLabels.use',
};

const ARTIFACT_VALUE_KEYS: Record<string, string> = {
  'Prompt + graph context': 'insights.artifactValues.promptAndGraph',
  'Command draft': 'insights.artifactValues.commandDraft',
  'Clinical Summary': 'insights.artifactValues.clinicalSummary',
  'Risk + family history': 'insights.artifactValues.riskFamilyHistory',
  'Evidence Gap Table': 'insights.artifactValues.evidenceGapTable',
  'Confirmed / uncertain / missing': 'insights.artifactValues.evidenceFormat',
  'Draft Report': 'insights.artifactValues.draftReport',
  'Clinical / care handoff': 'insights.artifactValues.clinicalCareHandoff',
  'Dynamics Summary': 'insights.artifactValues.dynamicsSummary',
  'Family therapist': 'insights.artifactValues.familyTherapist',
  'Hypothesis Draft': 'insights.artifactValues.hypothesisDraft',
  'Session planning': 'insights.artifactValues.sessionPlanning',
  'Inherited Risk Review': 'insights.artifactValues.inheritedRiskReview',
  'Genetic counselor': 'insights.artifactValues.geneticCounselor',
  'Genetics Data Checklist': 'insights.artifactValues.geneticsDataChecklist',
  'Counseling intake': 'insights.artifactValues.counselingIntake',
  'Care Coordination Summary': 'insights.artifactValues.careCoordinationSummary',
  'Case worker': 'insights.artifactValues.caseWorker',
  'Service/Safety Checklist': 'insights.artifactValues.serviceSafetyChecklist',
  'Intake follow-up': 'insights.artifactValues.intakeFollowUp',
};

const SECTION_TITLE_KEYS: Record<string, string> = {
  'Graph coverage': 'insights.sections.graphCoverage',
  'Top health and culture signals': 'insights.sections.topHealthCulture',
  'Evidence gaps': 'insights.sections.evidenceGaps',
  'Recommended validation steps': 'insights.sections.recommendedValidation',
  'Report outline': 'insights.sections.reportOutline',
  'Change summary': 'insights.sections.changeSummary',
};

const DEFAULT_TITLE_KEYS: Record<string, string> = {
  Analysis: 'insights.defaultTitles.analysis',
  Evidence: 'insights.defaultTitles.evidence',
  'Report Preview': 'insights.defaultTitles.reportPreview',
  'Proposed Changes': 'insights.defaultTitles.proposedChanges',
};

const TEMPLATE_TITLE_TO_ANALYZE_KEY: Record<string, string> = {
  'Clinical Family Summary': 'clinical-summary',
  'Evidence & Data Gaps': 'evidence-gaps',
  'Draft Case Report': 'report-draft',
  'Family Dynamics Review': 'family-dynamics',
  'Therapy Hypotheses': 'therapy-hypotheses',
  'Inherited Risk Review': 'inherited-risk',
  'Genetics Data Gaps': 'genetics-data-gaps',
  'Care Coordination Summary': 'care-plan',
  'Service & Safety Gaps': 'service-gaps',
};

const TEMPLATE_DESCRIPTION_TO_ANALYZE_KEY: Record<string, string> = {
  'Health patterns, key risks, and missing medical facts.': 'clinical-summary',
  'Facts captured vs uncertain or missing details.': 'evidence-gaps',
  'Structured draft for handoff or documentation.': 'report-draft',
  'Relationship patterns, stressors, and support structure.': 'family-dynamics',
  'Working hypotheses and targeted follow-up questions.': 'therapy-hypotheses',
  'Likely hereditary clusters and triage priorities.': 'inherited-risk',
  'Missing diagnoses, onset ages, and test evidence.': 'genetics-data-gaps',
  'Household structure, vulnerability signals, and actions.': 'care-plan',
  'Potential unmet needs and clarifications required.': 'service-gaps',
};

const DESCRIPTION_KEYS: Record<string, string> = {
  'Deterministic overview of structure, relationships, and major signals.': 'insights.defaultDescriptions.analysis',
  'Data quality review with confirmed gaps and next verification steps.': 'insights.defaultDescriptions.evidence',
  'Draft structure of a clinical report from current diagram evidence.': 'insights.defaultDescriptions.reportPreview',
  'Review and apply structural updates generated from your prompt and current graph context.':
    'insights.defaultDescriptions.proposedChanges',
};

const STATUS_NOTE_KEYS: Record<string, string> = {
  'Analysis complete.': 'insights.status.analysisComplete',
  'Evidence analysis complete.': 'insights.status.evidenceComplete',
  'Report preview ready.': 'insights.status.reportPreviewReady',
  'Running analysis now. Results will appear in chat and can be refined with follow-up prompts.':
    'insights.status.runningAnalysis',
  'Analysis complete. You can export this or ask for deeper breakdowns.': 'insights.status.analysisDoneExport',
  'Summary generated': 'insights.status.summaryGenerated',
  'Proposed changes prepared for review.': 'insights.status.proposedPrepared',
  'No actionable changes were generated.': 'insights.status.noActionableChanges',
};

const APPLY_SUMMARY_KEYS: Record<string, string> = {
  'Pending approval before command execution.': 'insights.proposedChanges.pendingApproval',
  'No commands were generated from the current prompt.': 'insights.proposedChanges.noCommands',
};

const REFINE_HINT_KEYS: Record<string, string> = {
  'Refine this proposal by narrowing scope, reducing uncertainty, or preserving only high-confidence changes.':
    'insights.proposedChanges.refineHint',
};

const FOLLOW_UP_KEYS: Record<string, string> = {
  'Add missing diagnosis ages and causes of death where known.': 'insights.followUps.diagnosisAges',
  'Confirm uncertain relationship links for disconnected people.': 'insights.followUps.confirmLinks',
  'Normalize health/culture tags to your preferred controlled vocabulary.': 'insights.followUps.normalizeTags',
  'Confirm whether these changes should be applied as-is.': 'insights.proposedChanges.followUp1',
  'Ask for a narrower update if only part of this proposal is needed.': 'insights.proposedChanges.followUp2',
};

const REPORT_OUTLINE_ITEM_KEYS: Record<string, string> = {
  'Family structure and key members': 'insights.reportOutline.familyStructure',
  'Medical and cultural patterns': 'insights.reportOutline.medicalCultural',
  'Known uncertainties and next actions': 'insights.reportOutline.uncertainties',
};

function insightNoneLabel(t: InsightPanelTranslateFn): string {
  return t('insights.none');
}

export function localizeInsightArtifactLabel(label: string, t: InsightPanelTranslateFn): string {
  const key = ARTIFACT_LABEL_KEYS[label];
  return key ? t(key) : label;
}

export function localizeInsightArtifactValue(value: string, t: InsightPanelTranslateFn): string {
  const key = ARTIFACT_VALUE_KEYS[value];
  return key ? t(key) : value;
}

export function localizeInsightSectionTitle(title: string, t: InsightPanelTranslateFn): string {
  const key = SECTION_TITLE_KEYS[title];
  return key ? t(key) : title;
}

export function localizeInsightSectionItem(item: string, t: InsightPanelTranslateFn): string {
  let m = item.match(/^Relationship types: (.*)$/);
  if (m) {
    const raw = m[1].trim();
    const detail = raw === 'none' ? insightNoneLabel(t) : raw;
    return t('insights.items.relationshipTypes', { detail });
  }
  m = item.match(/^Disconnected people: (\d+)$/);
  if (m) {
    return t('insights.items.disconnectedPeople', { count: Number.parseInt(m[1], 10) });
  }
  m = item.match(/^Deceased marked: (\d+)$/);
  if (m) {
    return t('insights.items.deceasedMarked', { count: Number.parseInt(m[1], 10) });
  }
  m = item.match(/^Health tags: (.*)$/);
  if (m) {
    const raw = m[1].trim();
    const detail = raw === 'none' ? insightNoneLabel(t) : raw;
    return t('insights.items.healthTags', { detail });
  }
  m = item.match(/^Culture tags: (.*)$/);
  if (m) {
    const raw = m[1].trim();
    const detail = raw === 'none' ? insightNoneLabel(t) : raw;
    return t('insights.items.cultureTags', { detail });
  }

  m = item.match(/^(\d+) person records missing a name$/);
  if (m) {
    return t('insights.evidenceItems.missingNames', { count: Number.parseInt(m[1], 10) });
  }
  if (item === 'No missing names detected') {
    return t('insights.evidenceItems.noMissingNames');
  }
  m = item.match(/^(\d+) people have unknown sex marker$/);
  if (m) {
    return t('insights.evidenceItems.unknownSex', { count: Number.parseInt(m[1], 10) });
  }
  if (item === 'No unknown sex markers detected') {
    return t('insights.evidenceItems.noUnknownSex');
  }
  m = item.match(/^(\d+) people are not connected by relationships$/);
  if (m) {
    return t('insights.evidenceItems.disconnected', { count: Number.parseInt(m[1], 10) });
  }
  if (item === 'All people are connected to at least one relationship') {
    return t('insights.evidenceItems.allConnected');
  }
  m = item.match(/^(\d+) people have no parent\/child linkage$/);
  if (m) {
    return t('insights.evidenceItems.noParentChild', { count: Number.parseInt(m[1], 10) });
  }
  if (item === 'Parent/child linkage exists for all people') {
    return t('insights.evidenceItems.allParentChild');
  }

  const roKey = REPORT_OUTLINE_ITEM_KEYS[item];
  if (roKey) {
    return t(roKey);
  }

  m = item.match(/^Additions: (\d+)$/);
  if (m) {
    return t('insights.metrics.additions', { count: Number.parseInt(m[1], 10) });
  }
  m = item.match(/^Removals: (\d+)$/);
  if (m) {
    return t('insights.metrics.removals', { count: Number.parseInt(m[1], 10) });
  }
  m = item.match(/^Moves: (\d+)$/);
  if (m) {
    return t('insights.metrics.moves', { count: Number.parseInt(m[1], 10) });
  }
  m = item.match(/^Updates: (\d+)$/);
  if (m) {
    return t('insights.metrics.updates', { count: Number.parseInt(m[1], 10) });
  }

  return item;
}

export function localizeInsightFollowUp(item: string, t: InsightPanelTranslateFn): string {
  const key = FOLLOW_UP_KEYS[item];
  return key ? t(key) : item;
}

export function localizeInsightTitle(title: string, t: InsightPanelTranslateFn): string {
  const defaultKey = DEFAULT_TITLE_KEYS[title];
  if (defaultKey) {
    return t(defaultKey);
  }
  const analyzeId = TEMPLATE_TITLE_TO_ANALYZE_KEY[title];
  if (analyzeId) {
    return t(`assistant.analyzeTemplates.${analyzeId}.title`);
  }
  return title;
}

export function localizeInsightDescription(text: string | undefined, t: InsightPanelTranslateFn): string | undefined {
  if (!text) return text;
  const key = DESCRIPTION_KEYS[text];
  if (key) {
    return t(key);
  }
  const analyzeId = TEMPLATE_DESCRIPTION_TO_ANALYZE_KEY[text];
  if (analyzeId) {
    return t(`assistant.analyzeTemplates.${analyzeId}.description`);
  }
  return text;
}

export function localizeInsightStatusNote(text: string | undefined, t: InsightPanelTranslateFn): string | undefined {
  if (!text) return text;
  const key = STATUS_NOTE_KEYS[text];
  return key ? t(key) : text;
}

export function localizeInsightApplySummary(text: string | undefined, t: InsightPanelTranslateFn): string | undefined {
  if (!text) return text;
  const key = APPLY_SUMMARY_KEYS[text];
  return key ? t(key) : text;
}

export function localizeInsightRefineHint(text: string | undefined, t: InsightPanelTranslateFn): string | undefined {
  if (!text) return text;
  const key = REFINE_HINT_KEYS[text];
  return key ? t(key) : text;
}
